import { useState, useEffect } from "react";

type ValidatorFn<T = unknown> = (value: T, allValues?: Record<string, unknown>, rowIndex?: number) => string | null;
interface UseFormValidationArrayProps<T> {
  initialValues: T[];
  rules: Partial<Record<keyof T, ValidatorFn[]>>;
  defaultValues?: Partial<T>[];
}

export function useFormValidation<T extends Record<string, unknown>>({
  initialValues,
  rules,
  defaultValues,
}: UseFormValidationArrayProps<T>) {

  const mergedDefaults = defaultValues
    ? initialValues.map((item, i) => ({ ...item, ...(defaultValues[i] || {}) }))
    : initialValues;

  const [values, setValues] = useState<T[]>(mergedDefaults);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>[]>([]);

  const runValidators = (validators: ValidatorFn[] | undefined, value: unknown, allValues?: Record<string, unknown>, rowIndex?: number) => {
    if (!validators) {return null;}
    for (const validator of validators) {
      const err = validator(value, allValues, rowIndex);
      if (err) {return err;}
    }
    return null;
  };

  const validateField = (index: number, name: keyof T, value: unknown) => {
    const fieldValidators = rules[name];
    if (!fieldValidators) {return null;}

    const allValues = values[index];
    const error = runValidators(fieldValidators, value, allValues, index);

    setErrors(prev => {
      const newErrors = [...prev];
      newErrors[index] = { ...newErrors[index], [name]: error };
      return newErrors;
    });

    return error;
  };

  const handleChangeFormField = (index: number, name: keyof T) => {
    return (
      e:
        | React.ChangeEvent<HTMLInputElement>
        | React.ChangeEvent<HTMLSelectElement>
        | React.ChangeEvent<HTMLTextAreaElement>
    ) => {
      const target = e.target;
      let value: unknown;

      if (target.type === 'checkbox') {
        value = (target as HTMLInputElement).checked;
      } else if (target.type === 'number') {
        value = (target as HTMLInputElement).valueAsNumber;
      } else {
        value = target.value;
      }

      setValues(prev => {
        const newValues = [...prev];
        newValues[index] = { ...newValues[index], [name]: value };
        return newValues;
      });

      validateField(index, name, value);
    };
  };

  const validateAll = (tempValues?: T[]) => {
  const newErrors: Partial<Record<keyof T, string>>[] = [];
  const valuesToCheck = tempValues?.length ? tempValues : values;

  for (let i = 0; i < valuesToCheck.length; i++) {
    const item = valuesToCheck[i];
    const itemErrors: Partial<Record<keyof T, string>> = {};

    const ruleKeys = Object.keys(rules) as (keyof T)[];
    for (const key of ruleKeys) {
      const value = item[key];
      const fieldValidators = rules[key];
      const error = runValidators(fieldValidators, value, item, i);

      if (error) {
        itemErrors[key] = error;
      }
    }

    newErrors[i] = itemErrors;
  }

  setErrors(newErrors);
  return newErrors;
};

  const addItem = (item: T) => {
    setValues(prev => [...prev, item]);
    setErrors(prev => [...prev, {}]);
  };

  const removeItem = (index: number) => {
    setValues(prev => prev.filter((_, i) => i !== index));
    setErrors(prev => prev.filter((_, i) => i !== index));
  };

  const resetErrors = () => {
    setErrors(values.map(() => ({})));
  };

  useEffect(() => {
    if (defaultValues) {
      setValues(prev =>
        prev.map((item, i) => ({ ...item, ...(defaultValues[i] || {}) }))
      );
    }
  }, [defaultValues]);

  const isValid = errors.every(item => Object.values(item ?? {}).every(err => !err));

  return {
    values,
    errors,
    handleChangeFormField,
    validateField,
    validateAll,
    isValid,
    addItem,
    removeItem,
    resetErrors,
    setValues,
    setErrors,
  };
}
